/* eslint-disable react/no-array-index-key */
import { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';

import { roundTo } from '../../../../../../../helpers/helpers';
import capitalize from '../../../../../../../helpers/capitalize';
import convertPermalinkToPath from '../../../../../../../helpers/urlHelpers';
import { UKDisableMessage } from '../../../../../../../data/ukPrivateCompanyInsights';

import ConditionalTooltip from '../../../../../../UKPrivateCompanyInsights/CommonComponent/ConditionalTooltip';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const TabTableSection = (props) => {
  const {
    currentPage, pageSize, currentData, hasInsightsAccess, hasInsightsTrialAccess,
    sortField, sortOrder, onSort,
  } = props;
  const tableBodyRef = useRef();
  const currentYear = moment().format('YYYY');
  const width = useSelector(({ common }) => (
    common.width
  ));
  const yearStartCount = width <= 767 ? 1 : 2;
  const disableMessage = UKDisableMessage(true);

  return (
    <div className={cx('change-investors', { no_access: !hasInsightsAccess })}>
      <div className={Styles['change-investors-header']}>
        <div className={Styles['change-investors-row']}>
          <div className={cx('change-investors-cell', 'rowNumber')}>#</div>
          <div className={cx('change-investors-cell', 'investorName')}>Investor’s Name</div>
          <div className={cx('change-investors-cell', 'classOfShare')}>Class of Shares</div>
          {currentData.currentYearHeader
            .slice(0, yearStartCount)
            .map((year) => (
              <div key={year} className={cx('change-investors-cell', 'year')}>
                {year}
                {year === currentYear ? ' YTD' : ''}
              </div>
            ))}
          <div className={cx('change-investors-cell', 'expandIcon')} />
        </div>
        <div
          className={cx('change-investors-row', 'total_row')}
        >
          <div className={cx('change-investors-cell', 'rowNumber')} />
          <div className={cx('change-investors-cell', 'investorName')} />
          <div className={cx('change-investors-cell', 'classOfShare')} />
          {currentData.currentYearHeader
            .slice(0, yearStartCount)
            .map((year, i) => (
              <>
                <div
                  className={cx('change-investors-cell', 'numberOfShares', { highLight: i % 2 === 0 })}
                  key={`${year}_numberOfShares_${i}`}
                >
                  {i === 0 ? (
                    <div className={Styles['table-sorting']} onClick={() => onSort(`year_${year}_numberOfShares`)}>
                      <div>
                        Shares
                      </div>
                      <ConditionalTooltip
                        tooltipProps={{
                          hasBigScreenDesign: true,
                          isCentered: true,
                          dynamicPosition: true,
                          contentStyle: { left: 0 },
                          className: Styles['change-investors-cell-tooltip'],
                        }}
                        tooltipMessage={disableMessage}
                        showTooltip={hasInsightsTrialAccess}
                      >
                        <div className={cx('sort-arrows')}>
                          {(sortField !== `year_${year}_numberOfShares` || sortOrder === 'asc') && (
                            <FontAwesomeIcon icon={faCaretUp} className={cx('asc')} />
                          )}
                          {(sortField !== `year_${year}_numberOfShares` || sortOrder === 'desc') && (
                            <FontAwesomeIcon icon={faCaretDown} className={cx('desc')} />
                          )}
                        </div>
                      </ConditionalTooltip>
                    </div>
                  ) : (
                    <>
                      Shares
                    </>
                  )}
                </div>
                {currentData.currentYearHeader.length !== i + 1 && (
                  <div
                    className={cx('change-investors-cell', 'annualPercentageChangeInNumberOfShares', { highLight: i % 2 === 0 })}
                    key={`${year}_annualPercentageChangeInNumberOfShares_${i}`}
                  >
                    {i === 0 ? (
                      <div className={Styles['table-sorting']} onClick={() => onSort(`year_${year}_annualPercentageChangeInNumberOfShares`)}>
                        <div>
                          Change
                        </div>
                        <ConditionalTooltip
                          tooltipProps={{
                            hasBigScreenDesign: true,
                            isCentered: true,
                            dynamicPosition: true,
                            contentStyle: { left: 0 },
                            className: Styles['change-investors-cell-tooltip'],
                          }}
                          tooltipMessage={disableMessage}
                          showTooltip={hasInsightsTrialAccess}
                        >
                          <div className={cx('sort-arrows')}>
                            {(sortField !== `year_${year}_annualPercentageChangeInNumberOfShares` || sortOrder === 'asc') && (
                              <FontAwesomeIcon icon={faCaretUp} className={cx('asc')} />
                            )}
                            {(sortField !== `year_${year}_annualPercentageChangeInNumberOfShares` || sortOrder === 'desc') && (
                              <FontAwesomeIcon icon={faCaretDown} className={cx('desc')} />
                            )}
                          </div>
                        </ConditionalTooltip>
                      </div>
                    ) : (
                      <>
                        Change
                      </>
                    )}
                  </div>
                )}
              </>
            ))}
          <div className={cx('change-investors-cell', 'expandIcon')} />
        </div>
      </div>
      {!hasInsightsAccess && (
        <div className={Styles['change-investors-access']}>
          <Link to="/pricing">Upgrade to UK Insights</Link>
          {' '}
          to use this feature.
        </div>
      )}
      <div className={Styles['change-investors-body']} ref={tableBodyRef}>
        {currentData.data
          .slice(((currentPage - 1) * pageSize), (((currentPage - 1) * pageSize) + pageSize))
          .map((obj, i) => (
            <TableRow
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              currentData={obj}
              currentRow={((currentPage - 1) * pageSize) + (i + 1)}
              currentYearHeader={currentData.currentYearHeader}
              width={width}
              yearStartCount={yearStartCount}
            />
          ))}
      </div>
    </div>
  );
};

const TableRow = (props) => {
  const {
    currentData, currentRow, currentYearHeader, width, yearStartCount,
  } = props;
  const [isExpand, setIsExpand] = useState(false);
  const hasMoreData = currentYearHeader.length > 2
    || (width <= 767 && currentYearHeader.length > 1);

  const updateIsExpande = () => {
    if (!hasMoreData) return;
    setIsExpand(!isExpand);
  };

  return (
    <div className={cx('change-investors-row-holder')}>
      <div className={Styles['change-investors-row']}>
        <div className={cx('change-investors-cell', 'rowNumber')}>
          {currentRow}
        </div>
        <div className={cx('change-investors-cell', 'investorName')}>
          {currentData.investorPermalink ? (
            <Link to={convertPermalinkToPath(currentData.investorPermalink)}>
              {currentData.investorName || '-'}
            </Link>
          ) : (
            <>
              {currentData.investorName || '-'}
            </>
          )}
        </div>
        <div className={cx('change-investors-cell', 'classOfShare')}>
          {capitalize(currentData.classOfShares)}
        </div>
        {currentYearHeader
          .slice(0, yearStartCount)
          .map((year, i) => (
            <>
              <div
                className={cx('change-investors-cell', 'numberOfShares', { highLight: i % 2 === 0 })}
                key={`${year}_numberOfShares_${i}`}
              >
                {(currentData[`year_${year}_numberOfShares`] || '').toLocaleString() || '-'}
              </div>
              {currentYearHeader.length !== i + 1 && (
                <div
                  className={cx('change-investors-cell', 'annualPercentageChangeInNumberOfShares', { highLight: i % 2 === 0 })}
                  key={`${year}_annualPercentageChangeInNumberOfShares_${i}`}
                >
                  {typeof currentData[`year_${year}_annualPercentageChangeInNumberOfShares`] === 'number'
                    ? `${roundTo(currentData[`year_${year}_annualPercentageChangeInNumberOfShares`], 2)}%` : '-'}
                </div>
              )}
            </>
          ))}
        <div
          className={cx('change-investors-cell', 'expandIcon')}
          onClick={updateIsExpande}
        >
          {hasMoreData && (
            <FontAwesomeIcon icon={isExpand ? faChevronUp : faChevronDown} />
          )}
        </div>
      </div>
      {isExpand && (
      <div className={cx('change-investors-extra')}>
        <div className={cx('change-investors-extra-years')}>
          {currentYearHeader
            .slice(yearStartCount)
            .map((year) => (
              <div key={year} className={cx('change-investors-extra-year')}>
                {year}
              </div>
            ))}
        </div>
        <div className={cx('change-investors-extra-holder')}>
          {currentYearHeader
            .slice(yearStartCount)
            .map((year, i) => (
              <>
                {i === 2 && (
                  <div className={cx('change-investors-extra-divider')} />
                )}
                <div key={year} className={cx('change-investors-extra-outer')}>
                  <div className={cx('change-investors-extra-year')}>
                    {year}
                  </div>
                  <div className={cx('change-investors-extra-cell', 'numberOfShares')}>
                    <div className={cx('change-investors-extra-title')}>
                      Shares
                    </div>
                    <div className={cx('change-investors-extra-value')}>
                      {(currentData[`year_${year}_numberOfShares`] || '').toLocaleString() || '-'}
                    </div>
                  </div>
                  {currentYearHeader.length !== i + 1 && (
                    <div className={cx('change-investors-extra-cell', 'annualPercentageChangeInNumberOfShares')}>
                      <div className={cx('change-investors-extra-title')}>
                        Change
                      </div>
                      <div className={cx('change-investors-extra-value')}>
                        {typeof currentData[`year_${year}_annualPercentageChangeInNumberOfShares`] === 'number'
                          ? `${roundTo(currentData[`year_${year}_annualPercentageChangeInNumberOfShares`], 2)}%` : '-'}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ))}
        </div>
      </div>
      )}
    </div>
  );
};

export default TabTableSection;
